import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Server, 
  Users, 
  Activity, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  TrendingUp
} from 'lucide-react';
import axios from 'axios';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import StatusCard from '../../components/StatusCard';
import Loader from '../../components/Loader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [serverStatus, setServerStatus] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [stats, setStats] = useState({ staff: 0, rules: 0, incidents: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statusRes, historyRes, staffRes, rulesRes, incidentsRes] = await Promise.all([
          axios.get(`${API}/server-status`),
          axios.get(`${API}/status-history?limit=24`),
          axios.get(`${API}/staff`),
          axios.get(`${API}/rules`),
          axios.get(`${API}/incidents?limit=10`)
        ]);
        
        setServerStatus(statusRes.data);
        setStatusHistory(historyRes.data.reverse());
        setStats({
          staff: staffRes.data.length,
          rules: rulesRes.data.length,
          incidents: incidentsRes.data.filter(i => i.status !== 'resolved').length
        });
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const chartData = statusHistory.map((entry) => ({
    time: new Date(entry.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
    players: entry.players
  }));

  if (loading) {
    return <Loader text="Chargement du dashboard..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-2xl text-white uppercase tracking-wider">
          Dashboard
        </h1>
        <p className="text-gray-400">Vue d'ensemble du serveur</p>
      </div>

      {/* Server Status Banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`glass rounded-xl p-6 border ${
          serverStatus?.online ? 'border-green-500/30' : 'border-red-500/30'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
              serverStatus?.online ? 'bg-green-500/20' : 'bg-red-500/20'
            }`}>
              {serverStatus?.online ? (
                <CheckCircle className="w-6 h-6 text-green-400" />
              ) : (
                <XCircle className="w-6 h-6 text-red-400" />
              )}
            </div>
            <div>
              <h2 className={`font-heading font-bold text-xl uppercase tracking-wider ${
                serverStatus?.online ? 'text-green-400' : 'text-red-400'
              }`}>
                {serverStatus?.online ? 'Serveur en ligne' : 'Serveur hors ligne'}
              </h2>
              <p className="text-gray-400 text-sm">
                {serverStatus?.players || 0}/{serverStatus?.max_players || 100} joueurs • 
                {serverStatus?.ping || 0}ms • 
                {serverStatus?.uptime_percentage || 100}% uptime
              </p>
            </div>
          </div>
          
          {serverStatus?.online && (
            <div className="hidden sm:flex items-center gap-2 text-green-400">
              <div className="w-2 h-2 rounded-full bg-green-400 pulse-online" />
              <span className="text-sm">En ligne</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatusCard
          title="Joueurs"
          value={`${serverStatus?.players || 0}`}
          subtitle={`sur ${serverStatus?.max_players || 100}`}
          icon={Users}
          status={serverStatus?.online ? 'online' : 'offline'}
        />
        <StatusCard
          title="Staff"
          value={stats.staff}
          subtitle="membres"
          icon={Users}
          status="default"
        />
        <StatusCard
          title="Règles"
          value={stats.rules}
          subtitle="actives"
          icon={Activity}
          status="default"
        />
        <StatusCard
          title="Incidents"
          value={stats.incidents}
          subtitle="en cours"
          icon={AlertTriangle}
          status={stats.incidents > 0 ? 'warning' : 'online'}
        />
      </div>

      {/* Players Chart */}
      {chartData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass rounded-xl p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-violet-400" />
              Activité des joueurs (24h)
            </h3>
          </div>
          
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="dashboardGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis 
                  dataKey="time" 
                  stroke="#4b5563" 
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  stroke="#4b5563" 
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#0a0a0a', 
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px'
                  }}
                  labelStyle={{ color: '#9ca3af' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="players" 
                  stroke="#8b5cf6" 
                  strokeWidth={2}
                  fill="url(#dashboardGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <motion.a
          href="/admin/settings"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass rounded-xl p-6 hover:border-violet-500/30 transition-all group"
          data-testid="quick-settings"
        >
          <Server className="w-8 h-8 text-violet-400 mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-heading font-bold text-white uppercase tracking-wider mb-1">
            Paramètres
          </h3>
          <p className="text-gray-400 text-sm">Configurer le serveur</p>
        </motion.a>

        <motion.a
          href="/admin/staff"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="glass rounded-xl p-6 hover:border-violet-500/30 transition-all group"
          data-testid="quick-staff"
        >
          <Users className="w-8 h-8 text-blue-400 mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-heading font-bold text-white uppercase tracking-wider mb-1">
            Staff
          </h3>
          <p className="text-gray-400 text-sm">Gérer l'équipe</p>
        </motion.a>

        <motion.a
          href="/admin/incidents"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="glass rounded-xl p-6 hover:border-violet-500/30 transition-all group"
          data-testid="quick-incidents"
        >
          <AlertTriangle className="w-8 h-8 text-yellow-400 mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-heading font-bold text-white uppercase tracking-wider mb-1">
            Incidents
          </h3>
          <p className="text-gray-400 text-sm">Gérer les incidents</p>
        </motion.a>
      </div>
    </div>
  );
};

export default AdminDashboard;
